import { useEffect, useRef, useState } from 'react';

/**
 * Rate-limit how often a rapidly-changing value is handed to expensive renders.
 *
 * The backend broadcasts prices every 500ms and the WebSocket hook batches at
 * 200ms, so `livePrices` changes several times a second. Each change makes the
 * matrix recompute all N^2 cells — 10,506 of them for a 103-ticker list — which
 * is a lot of browser CPU for updates faster than anyone can read.
 *
 * Leading edge fires immediately (the first tick after a quiet period is not
 * delayed) and the trailing edge guarantees the newest value is never dropped.
 *
 * @param {*} value
 * @param {number} delayMs minimum gap between propagated updates
 */
export default function useThrottledValue(value, delayMs = 1000) {
  const [throttled, setThrottled] = useState(value);
  const lastEmittedAt = useRef(0);
  const latestValue = useRef(value);
  const timerRef = useRef(null);

  latestValue.current = value;

  useEffect(() => {
    const elapsed = Date.now() - lastEmittedAt.current;

    if (elapsed >= delayMs) {
      lastEmittedAt.current = Date.now();
      setThrottled(value);
      return undefined;
    }

    // Already have a trailing update queued — it will pick up latestValue.
    if (timerRef.current) return undefined;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      lastEmittedAt.current = Date.now();
      setThrottled(latestValue.current);
    }, delayMs - elapsed);

    return undefined;
  }, [value, delayMs]);

  // Only clears on unmount; the effect above deliberately keeps its pending
  // trailing timer alive across value changes.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return throttled;
}
